<script setup>
import { useI18n } from 'vue-i18n'
import { getLanguageService } from '@/core/services/language.service.js'
import { useLanguageStore } from '@/core/stores/language.store.js'
import { useAuthStore } from '@/modules/auth/stores/auth.store.js'
import flagGb from '@/shared/assets/svg/flags/gb.svg'

const { t } = useI18n()

const isOpen = ref(false)
const menu = ref()
const languageStore = useLanguageStore()
const authStore = useAuthStore()
const isLoading = ref(false)

const languages = computed(() => languageStore.getActiveLocales)

const currentLanguageLabel = computed(() => {
  const language = languages.value.find(
    lang => lang.key === languageStore.currentLanguage,
  )
  return language?.name || t('common.languages.default')
})

const currentFlag = computed(() => {
  const language = languages.value.find(
    lang => lang.key === languageStore.currentLanguage,
  )
  return language?.flag || flagGb
})

function displayContextMenu(event) {
  if (isLoading.value)
    return
  menu.value.toggle(event)
}

async function handleLanguageClick(lang) {
  if (lang === languageStore.currentLanguage)
    return

  languageStore.setLanguage(lang)
  if (authStore.userData?.id) {
    try {
      isLoading.value = true
      menu.value.hide()
      const languageService = getLanguageService()
      await languageService.updateDefaultLanguage(authStore.userData.id, lang)
      location.reload()
    }
    catch (error) {
      console.error('Erro ao atualizar idioma no backend:', error)
    }
  }
}

function isCurrentLanguage(langKey) {
  return langKey === languageStore.currentLanguage
}

function popoverShow() {
  isOpen.value = true
}

function popoverHide() {
  isOpen.value = false
}
</script>

<template>
  <div
    class="w-28 flex justify-between px-2.5 py-3 rounded-md bg-dss-menu-active-bg text-white cursor-pointer h-11"
    @click="displayContextMenu($event)"
  >
    <div class="flex items-center gap-2">
      <div>
        <img
          :src="currentFlag"
          :alt="$t('common.languages.flag-alt')"
          class=""
        >
      </div>
      <span class="text-sm font-medium uppercase">
        {{ currentLanguageLabel }}
      </span>
    </div>
    <div
      v-if="isLoading"
      class="flex items-center justify-center"
    >
      <i class="pi pi-spin pi-spinner text-xl text-primary-500 -my-1" />
    </div>
    <i
      v-else
      class="pi pi-chevron-down text-sm transition-transform duration-300 ease-in-out"
      :class="{ 'rotate-180': isOpen }"
    />
  </div>
  <Popover
    ref="menu"
    class="w-64 max-w-[calc(100vw-2rem)] bg-white! before:hidden after:hidden mt-2! drop-shadow-lg! rounded-2xl! rounded-tl-none!"
    @show="popoverShow"
    @hide="popoverHide"
  >
    <ul class="flex flex-col gap-2">
      <li
        v-for="language in languages"
        :key="language.key"
        class="group"
        :class="[isCurrentLanguage(language.key) ? 'cursor-default' : 'cursor-pointer']"
        @click="handleLanguageClick(language.key)"
      >
        <div class="flex min-w-0 items-center gap-2">
          <div class="w-5 shrink-0">
            <img
              :src="language.flag"
              :alt="$t('common.languages.flag-alt')"
              class="size-5"
            >
          </div>
          <span
            class="min-w-0 truncate text-xs font-medium"
            :title="language.displayLabel"
            :class="[isCurrentLanguage(language.key) ? '' : 'group-hover:underline'] "
          >{{
            language.displayLabel
          }}</span>
        </div>
      </li>
    </ul>
  </Popover>
</template>

<style scoped></style>
